import React, { useMemo, useState } from 'react';
import { Wallet, Plus, Trash2, FileText, Search, Edit } from 'lucide-react';
import { useData } from '../context/DataContext';
import { confirmDialog } from '../lib/confirm';
import { toast } from 'sonner';

import { useT } from '../context/LanguageContext';
const eur = (n) => n.toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' });

export default function Betalingen() {

  const { t, lang } = useT();  const { betalingen, bonnen, klanten, createBetaling, updateBetaling, deleteBetaling, refreshAll } = useData();
  const [filter, setFilter] = useState('');
  const [tab, setTab] = useState('alle');
  const [addOpen, setAddOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [editBet, setEditBet] = useState(null); // betaling object voor inline-edit
  const [payBon, setPayBon] = useState(null); // openstaande bon → toon/bewerk gekoppelde betalingen
  const [newBet, setNewBet] = useState({ bonId: '', bedrag: '', fooi: '', methode: 'PIN', referentie: '', datum: new Date().toISOString().slice(0, 10) });

  const handleDelete = async (id) => {
    const targetId = id || selectedId;
    if (!targetId) { toast.error(t('Kies een betaling om te verwijderen', 'Kies een betaling om te verwijderen')); return; }
    const bet = betalingen.find(b => b.id === targetId);
    if (!bet) return;
    if (!(await confirmDialog(`${t('Betaling verwijderen?', 'Betaling verwijderen?')} (${eur(bet.bedrag)} - ${bet.bonNummer})`))) return;
    try {
      await deleteBetaling(targetId);
      toast.success(t('Betaling verwijderd', 'Betaling verwijderd'));
      setSelectedId(null);
      refreshAll();
    } catch (e) { toast.error(t('Verwijderen mislukt'), { description: e.response?.data?.detail || e.message }); }
  };

  const enriched = useMemo(() => betalingen.map(b => {
    const bon = bonnen.find(x => x.id === b.bonId);
    const klant = bon ? klanten.find(k => k.id === bon.klantId) : null;
    return { ...b, klantNaam: klant?.naam || '-', kenteken: bon?.kenteken || '' };
  }), [betalingen, bonnen, klanten]);

  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return enriched.filter(b => !f || b.bonNummer.toLowerCase().includes(f) || b.klantNaam.toLowerCase().includes(f) || b.kenteken.toLowerCase().includes(f) || b.methode.toLowerCase().includes(f));
  }, [enriched, filter]);

  const openBonnen = bonnen.filter(b => b.openstaand > 0);
  const totaalOntvangen = betalingen.reduce((s, b) => s + (Number(b.bedrag) || 0), 0);
  const totaalFooi = betalingen.reduce((s, b) => s + (Number(b.fooi) || 0), 0);
  const totaalOpenstaand = bonnen.reduce((s, b) => s + b.openstaand, 0);

  return (
    <div>
      <div className="cs-page-header">
        <div><h2>{t('page.payments.title')}</h2><p>{t('page.payments.sub')}</p></div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="cs-btn primary" onClick={() => setAddOpen(true)}><Plus size={14} /> {t('Betaling toevoegen', 'Betaling toevoegen')}</button>
          <button className="cs-btn" disabled={!selectedId} onClick={() => {
            const bet = betalingen.find(b => b.id === selectedId);
            if (!bet) { toast.error(t('Selecteer eerst een betaling', 'Selecteer eerst een betaling')); return; }
            const klant = klanten.find(k => k.id === bet.klantId);
            const w = window.open('', '_blank', 'width=720,height=900');
            if (!w) { toast.error(t('Sta pop-ups toe om het kwitantie-document te openen', 'Sta pop-ups toe om het kwitantie-document te openen')); return; }
            const html = `<!doctype html><html><head><meta charset="utf-8"><title>${t('Kwitantie', 'Kwitantie')} ${bet.bonNummer || bet.id}</title>
              <style>body{font-family:ui-sans-serif,system-ui,sans-serif;padding:24mm;color:#0f172a}
                h1{color:#2563eb;border-bottom:3px solid #2563eb;padding-bottom:8px;margin:0 0 16px}
                table{width:100%;border-collapse:collapse;margin-top:16px;font-size:13px}
                td{padding:6px 0;border-bottom:1px solid #e2e8f0}
                .lbl{color:#64748b;width:40%}
                .total{font-size:18px;font-weight:700;color:#16a34a}
                .fooi{color:#f59e0b}
                @page{size:A5}</style></head><body>
              <h1>${t('Betalingsbewijs / Kwitantie', 'Betalingsbewijs / Kwitantie')}</h1>
              <table>
                <tr><td class="lbl">${t('Datum')}</td><td>${new Date(b.datum).toLocaleDateString(  lang === 'en' ? 'en-GB' : 'nl-NL')}</td></tr>
                <tr><td class="lbl">${t('Bonnummer')}</td><td><b>${bet.bonNummer || '-'}</b></td></tr>
                <tr><td class="lbl">${t('Klant')}</td><td>${bet.klantNaam || klant?.naam || '-'}</td></tr>
                <tr><td class="lbl">${t('Kenteken')}</td><td>${bet.kenteken || '-'}</td></tr>
                <tr><td class="lbl">${t('Methode')}</td><td>${t(bet.methode, bet.methode)}</td></tr>
                ${bet.referentie ? `<tr><td class="lbl">${t('Referentie')}</td><td>${bet.referentie}</td></tr>` : ''}
                <tr><td class="lbl">${t('Ontvangen', 'Ontvangen')}</td><td class="total">€ ${(bet.bedrag || 0).toFixed(2)}</td></tr>
                ${bet.fooi > 0 ? `<tr><td class="lbl">${t('waarvan fooi', 'waarvan fooi')}</td><td class="fooi">€ ${bet.fooi.toFixed(2)}</td></tr>` : ''}
              </table>
              <p style="margin-top:30px;font-size:11px;color:#64748b">${t('Bedankt voor uw betaling.', 'Bedankt voor uw betaling.')}</p>
              </body></html>`;
            w.document.open(); w.document.write(html); w.document.close(); w.focus();
            setTimeout(() => { try { w.print(); } catch { /* print kan stilzwijgend falen in pop-ups */ } }, 250);
          }} data-testid="btn-betaling-document"><FileText size={14} /> {t('Document', 'Document')}</button>
          <button className="cs-btn danger" onClick={() => handleDelete()} data-testid="btn-delete-betaling"><Trash2 size={14} /> {t('Verwijderen')}</button>
        </div>
      </div>

      <div className="cs-three-col" style={{ marginBottom: 14 }}>
        <div className="cs-kpi"><div className="label">{t('Totaal ontvangen', 'Totaal ontvangen')}</div><div className="value" style={{ color: 'var(--cs-green)' }}>{eur(totaalOntvangen)}</div></div>
        <div className="cs-kpi"><div className="label">{t('Openstaand')}</div><div className="value" style={{ color: 'var(--cs-red)' }}>{eur(totaalOpenstaand)}</div></div>
        <div className="cs-kpi" data-testid="kpi-fooi"><div className="label">{t('Totaal fooi', 'Totaal fooi')}</div><div className="value" style={{ color: '#f59e0b' }}>{eur(totaalFooi)}</div></div>
      </div>

      <div className="cs-tabs">
        <button className={`cs-tab ${tab === 'alle' ? 'active' : ''}`} onClick={() => setTab('alle')}>{t('Alle betalingen', 'Alle betalingen')} ({betalingen.length})</button>
        <button className={`cs-tab ${tab === 'open' ? 'active' : ''}`} onClick={() => setTab('open')}>{t('Openstaande bonnen', 'Openstaande bonnen')} ({openBonnen.length})</button>
      </div>

      <div className="cs-toolbar">
        <div className="grow" style={{ position: 'relative' }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: 11, color: 'var(--cs-text-muted)' }} />
          <input className="cs-input" style={{ paddingLeft: 30 }} placeholder={t('Zoeken...', 'Zoeken...')} value={filter} onChange={e => setFilter(e.target.value)} />
        </div>
      </div>

      <div className="cs-card">
        {tab === 'alle' ? (
          <table className="cs-table">
            <thead><tr><th>{t('Bonnummer')}</th><th>{t('Datum')}</th><th>{t('Klant')}</th><th>{t('Kenteken')}</th><th>{t('Methode')}</th><th>{t('Referentie')}</th><th style={{textAlign:'right'}}>{t('Bedrag')}</th><th></th></tr></thead>
            <tbody>
              {filtered.map(b => (
                <tr key={b.id} className={b.id === selectedId ? 'selected' : ''} onClick={() => setSelectedId(b.id)} onDoubleClick={() => setEditBet({ ...b })} style={{ cursor: 'pointer' }} title={t('Dubbelklik om te bewerken', 'Dubbelklik om te bewerken')}>
                  <td data-label={t('Bonnummer')} style={{ fontFamily: 'ui-monospace, monospace', fontWeight: 600 }}>{b.bonNummer}</td>
                  <td data-label={t('Datum')}>{new Date(b.datum).toLocaleDateString(  lang === 'en' ? 'en-GB' : 'nl-NL')}</td>
                  <td data-label={t('Klant')}>{b.klantNaam}</td>
                  <td data-label={t('Kenteken')} style={{ fontFamily: 'ui-monospace, monospace' }}>{b.kenteken}</td>
                  <td data-label={t('Methode')}><span className={`cs-badge ${b.methode === 'PIN' ? 'blue' : b.methode === 'Bank' ? 'green' : 'gray'}`}>{t(b.methode, b.methode)}</span></td>
                  <td data-label={t('Referentie')} style={{ color: 'var(--cs-text-muted)', fontSize: 12 }}>{b.referentie || '-'}</td>
                  <td data-label={t('Bedrag')} style={{ textAlign: 'right', fontWeight: 600, color: 'var(--cs-green)' }}>
                    {eur(b.bedrag)}
                    {b.fooi > 0 && (
                      <div style={{ fontSize: 10, fontWeight: 400, color: '#f59e0b' }} data-testid={`bet-fooi-${b.id}`}>
                  {t('incl.', 'incl.')} {eur(b.fooi)} {t('fooi', 'fooi')}
                      </div>
                    )}
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button className="cs-btn ghost" style={{ padding: 4 }} title={t('Bewerken')} data-testid={`edit-betaling-${b.id}`} onClick={(e) => { e.stopPropagation(); setEditBet({ ...b }); }}><Edit size={11} /></button>
                    <button className="cs-btn ghost danger" style={{ padding: 4 }} title={t('Verwijderen')} data-testid={`del-betaling-${b.id}`} onClick={(e) => { e.stopPropagation(); handleDelete(b.id); }}><Trash2 size={11} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table className="cs-table">
            <thead><tr><th>{t('Nummer')}</th><th>{t('Datum')}</th><th>{t('Klant')}</th><th>{t('Status')}</th><th style={{textAlign:'right'}}>{t('Totaal')}</th><th style={{textAlign:'right'}}>{t('Betaald')}</th><th style={{textAlign:'right'}}>{t('Openstaand')}</th></tr></thead>
            <tbody>
              {openBonnen.map(b => {
                const klant = klanten.find(k => k.id === b.klantId);
                return (
                  <tr key={b.id} onClick={() => setPayBon(b)} style={{ cursor: 'pointer' }} title={t('Klik om betalingen te bekijken/bewerken', 'Klik om betalingen te bekijken/bewerken')} data-testid={`open-bon-row-${b.id}`}>
                    <td data-label={t('Nummer')} style={{ fontFamily: 'ui-monospace, monospace', fontWeight: 600 }}>{b.nummer}</td>
                    <td data-label={t('Datum')}>{new Date(b.datum).toLocaleDateString(  lang === 'en' ? 'en-GB' : 'nl-NL')}</td>
                    <td data-label={t('Klant')}>{klant?.naam || '-'}</td>
                    <td data-label={t('Status')}><span className={`cs-badge ${b.status === 'DeelsBetaald' ? 'amber' : 'blue'}`}>{t(b.status, b.status)}</span></td>
                    <td data-label={t('Totaal')} style={{ textAlign: 'right' }}>{eur(b.totaal)}</td>
                    <td data-label={t('Betaald')} style={{ textAlign: 'right', color: 'var(--cs-green)' }}>{eur(b.betaald)}</td>
                    <td data-label={t('Openstaand')} style={{ textAlign: 'right', fontWeight: 600, color: 'var(--cs-red)' }}>{eur(b.openstaand)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {addOpen && (
        <div className="cs-modal-overlay" onClick={() => setAddOpen(false)}>
          <div className="cs-modal" onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>{t('Betaling toevoegen', 'Betaling toevoegen')}</h3>
            <div style={{ display: 'grid', gap: 10 }}>
              <div><label className="cs-section-title">{t('Bon')}</label>
                <select className="cs-input" value={newBet.bonId} onChange={e => setNewBet({ ...newBet, bonId: e.target.value })}>
                  <option value="">{t('-- Kies een bon --', '-- Kies een bon --')}</option>
                  {bonnen.filter(b => (b.openstaand || 0) > 0).map(b => {
                    const k = klanten.find(x => x.id === b.klantId);
                    return <option key={b.id} value={b.id}>{b.nummer} - {k?.naam || '-'} - {t('open', 'open')} {eur(b.openstaand)}</option>;
                  })}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10 }}>
                <div><label className="cs-section-title">{t('Datum')}</label><input type="date" className="cs-input" value={newBet.datum} onChange={e => setNewBet({ ...newBet, datum: e.target.value })} data-testid="bet-datum" /></div>
                <div>
                  <label className="cs-section-title">{t('Bedrag (€)')} <span style={{ fontSize: 10, color: 'var(--cs-text-muted)', fontWeight: 400 }}>({t('totaal ontvangen', 'totaal ontvangen')})</span></label>
                  <input className="cs-input" type="number" step="0.01" value={newBet.bedrag} data-testid="bet-bedrag"
                    onChange={e => {
                      const val = e.target.value;
                      const bonOpen = (bonnen.find(b => b.id === newBet.bonId)?.openstaand) || 0;
                      const num = parseFloat(val) || 0;
                      // Suggesteer fooi als bedrag > openstaand op deze bon
                      const suggestedFooi = bonOpen > 0 && num > bonOpen ? (num - bonOpen).toFixed(2) : newBet.fooi;
                      setNewBet({ ...newBet, bedrag: val, fooi: suggestedFooi });
                    }} />
                </div>
                <div>
                  <label className="cs-section-title">{t('Fooi (€)', 'Fooi (€)')} <span style={{ fontSize: 10, color: 'var(--cs-text-muted)', fontWeight: 400 }}>({t('optioneel', 'optioneel')})</span></label>
                  <input className="cs-input" type="number" step="0.01" value={newBet.fooi} data-testid="bet-fooi"
                    onChange={e => setNewBet({ ...newBet, fooi: e.target.value })} placeholder={t('0,00', '0,00')} />
                </div>
                <div><label className="cs-section-title">{t('Methode')}</label>
                  <select className="cs-input" value={newBet.methode} onChange={e => setNewBet({ ...newBet, methode: e.target.value })}>
                    <option value="PIN">PIN</option><option value="Bank">{t('Bank', 'Bank')}</option><option value="Contant">{t('Contant', 'Contant')}</option>
                  </select>
                </div>
              </div>
              {(() => {
                const bedrag = parseFloat(newBet.bedrag) || 0;
                const fooi = parseFloat(newBet.fooi) || 0;
                const naarBon = Math.max(0, bedrag - fooi);
                if (bedrag > 0) {
                  return (
                    <div style={{ fontSize: 12, color: 'var(--cs-text-muted)', padding: '6px 10px', background: 'var(--cs-card-alt)', borderRadius: 6 }} data-testid="bet-preview">
                      {t('Naar bon', 'Naar bon')}: <b style={{ color: 'var(--cs-green)' }}>{eur(naarBon)}</b>
                      {fooi > 0 && <> · {t('Fooi', 'Fooi')}: <b style={{ color: '#f59e0b' }}>{eur(fooi)}</b></>}
                    </div>
                  );
                }
                return null;
              })()}
              <div><label className="cs-section-title">{t('Referentie')}</label><input className="cs-input" value={newBet.referentie} onChange={e => setNewBet({ ...newBet, referentie: e.target.value })} /></div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
              <button className="cs-btn" onClick={() => setAddOpen(false)}>{t('Annuleren')}</button>
              <button className="cs-btn primary" onClick={async () => {
                const bon = bonnen.find(b => b.id === newBet.bonId);
                if (!bon) { toast.error(t('Kies een bon')); return; }
                const bedrag = parseFloat(newBet.bedrag);
                const fooi = parseFloat(newBet.fooi) || 0;
                if (!bedrag || bedrag <= 0) { toast.error(t('Ongeldig bedrag')); return; }
                if (fooi < 0 || fooi > bedrag) { toast.error(t('Fooi moet tussen 0 en het bedrag liggen', 'Fooi moet tussen 0 en het bedrag liggen')); return; }
                try {
                  await createBetaling({ bonId: bon.id, bonNummer: bon.nummer, datum: newBet.datum || new Date().toISOString().slice(0, 10), bedrag, fooi, methode: newBet.methode, referentie: newBet.referentie });
                  toast.success(t('Betaling toegevoegd'));
                  setAddOpen(false);
                  setNewBet({ bonId: '', bedrag: '', fooi: '', methode: 'PIN', referentie: '', datum: new Date().toISOString().slice(0, 10) });
                } catch { toast.error(t('Opslaan mislukt')); }
              }}>{t('Opslaan')}</button>
            </div>
          </div>
        </div>
      )}

      {payBon && (() => {
        const bonBetalingen = enriched.filter(x => x.bonId === payBon.id);
        const klant = klanten.find(k => k.id === payBon.klantId);
        return (
          <div className="cs-modal-overlay" onClick={() => setPayBon(null)}>
            <div className="cs-modal" onClick={e => e.stopPropagation()} data-testid="paybon-modal">
              <h3 style={{ marginTop: 0 }}>{t('Betalingen van bon', 'Betalingen van bon')} {payBon.nummer}</h3>
              <div style={{ fontSize: 12.5, color: 'var(--cs-text-muted)', marginBottom: 10 }}>
                {klant?.naam || '-'} · {t('Totaal', 'Totaal')} <b>{eur(payBon.totaal)}</b> · {t('Betaald', 'Betaald')} <b style={{ color: 'var(--cs-green)' }}>{eur(payBon.betaald)}</b> · {t('Openstaand', 'Openstaand')} <b style={{ color: 'var(--cs-red)' }}>{eur(payBon.openstaand)}</b>
              </div>

              {bonBetalingen.length === 0 ? (
                <div style={{ fontSize: 13, color: 'var(--cs-text-muted)', padding: '8px 0' }}>{t('Nog geen betalingen geregistreerd voor deze bon.', 'Nog geen betalingen geregistreerd voor deze bon.')}</div>
              ) : (
                <div style={{ display: 'grid', gap: 6 }}>
                  {bonBetalingen.map(p => (
                    <div key={p.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '8px 10px', border: '1px solid var(--cs-border)', borderRadius: 8 }} data-testid={`paybon-betaling-${p.id}`}>
                      <div style={{ fontSize: 13 }}>
                        <b style={{ color: 'var(--cs-green)' }}>{eur(p.bedrag)}</b>
                        {p.fooi > 0 && <span style={{ color: '#f59e0b', fontSize: 11.5 }}> · {t('fooi', 'fooi')} {eur(p.fooi)}</span>}
                        <span style={{ color: 'var(--cs-text-muted)' }}> · {t(p.methode, p.methode)} · {new Date(p.datum).toLocaleDateString(lang === 'en' ? 'en-GB' : 'nl-NL')}</span>
                      </div>
                      <div style={{ whiteSpace: 'nowrap' }}>
                        <button className="cs-btn ghost" style={{ padding: 4 }} title={t('Bewerken')} data-testid={`paybon-edit-${p.id}`} onClick={() => { setEditBet({ ...p }); setPayBon(null); }}><Edit size={13} /></button>
                        <button className="cs-btn ghost danger" style={{ padding: 4 }} title={t('Verwijderen')} data-testid={`paybon-del-${p.id}`} onClick={() => handleDelete(p.id)}><Trash2 size={13} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 16 }}>
                <button className="cs-btn primary" data-testid="paybon-add" onClick={() => {
                  setNewBet({ bonId: payBon.id, bedrag: '', fooi: '', methode: 'PIN', referentie: '', datum: new Date().toISOString().slice(0, 10) });
                  setPayBon(null); setAddOpen(true);
                }}><Plus size={14} /> {t('Betaling toevoegen', 'Betaling toevoegen')}</button>
                <button className="cs-btn" onClick={() => setPayBon(null)}>{t('Sluiten', 'Sluiten')}</button>
              </div>
            </div>
          </div>
        );
      })()}

      {editBet && (
        <div className="cs-modal-overlay" onClick={() => setEditBet(null)}>
          <div className="cs-modal" onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>{t('Betaling bewerken', 'Betaling bewerken')}</h3>
            <div style={{ display: 'grid', gap: 10 }}>
              <div style={{ fontSize: 12, color: 'var(--cs-text-muted)' }}>
                {t('Bon')}: <b>{editBet.bonNummer}</b> · {editBet.klantNaam || ''}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                <div><label className="cs-section-title">{t('Datum')}</label><input type="date" className="cs-input" value={editBet.datum?.slice(0, 10) || ''} onChange={e => setEditBet({ ...editBet, datum: e.target.value })} data-testid="edit-bet-datum" /></div>
                <div><label className="cs-section-title">{t('Bedrag (€)')}</label><input type="number" step="0.01" className="cs-input" value={editBet.bedrag} onChange={e => setEditBet({ ...editBet, bedrag: parseFloat(e.target.value) || 0 })} data-testid="edit-bet-bedrag" /></div>
                <div><label className="cs-section-title">{t('Fooi (€)', 'Fooi (€)')}</label><input type="number" step="0.01" className="cs-input" value={editBet.fooi || 0} onChange={e => setEditBet({ ...editBet, fooi: parseFloat(e.target.value) || 0 })} data-testid="edit-bet-fooi" /></div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div><label className="cs-section-title">{t('Methode')}</label>
                  <select className="cs-input" value={editBet.methode} onChange={e => setEditBet({ ...editBet, methode: e.target.value })}>
                    <option value="PIN">PIN</option><option value="Bank">{t('Bank', 'Bank')}</option><option value="Contant">{t('Contant', 'Contant')}</option>
                  </select>
                </div>
                <div><label className="cs-section-title">{t('Referentie')}</label><input className="cs-input" value={editBet.referentie || ''} onChange={e => setEditBet({ ...editBet, referentie: e.target.value })} /></div>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
              <button className="cs-btn" onClick={() => setEditBet(null)}>{t('Annuleren')}</button>
              <button data-testid="edit-bet-save" className="cs-btn primary" onClick={async () => {
                try {
                  await updateBetaling(editBet.id, {
                    datum: editBet.datum,
                    bedrag: parseFloat(editBet.bedrag) || 0,
                    fooi: parseFloat(editBet.fooi) || 0,
                    methode: editBet.methode,
                    referentie: editBet.referentie,
                  });
                  toast.success(t('Opgeslagen'));
                  setEditBet(null);
                  refreshAll();
                } catch (e) { toast.error(t('Opslaan mislukt'), { description: e.response?.data?.detail || e.message }); }
              }}>{t('Opslaan')}</button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .cs-modal-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.5); display: flex; align-items: center; justify-content: center; z-index: 100; }
        .cs-modal { background: #fff; border-radius: 10px; padding: 20px 22px; width: 520px; max-width: 92vw; box-shadow: 0 20px 60px rgba(0,0,0,0.25); }
      `}</style>
    </div>
  );
}
